import { useState, useEffect, useCallback } from 'react';
import { queueService } from '@/services/queueService';
import { websocketService } from '@/services/websocketService';
import { configService } from '@/services/configService';
import { WebSocketMessage } from '@/types/queue.types';
import { useAuth } from './useAuth';
import { debug } from '@/config/constants';

export interface UseQueueReturn {
  isInQueue: boolean;
  queuePosition: number;
  queueLength: number;
  estimatedWaitTime: number;
  queueId: string | null;
  queueJoinTime: number | null; // Timestamp when user joined queue
  isConnected: boolean;
  joinQueue: (featureName?: string) => Promise<void>;
  leaveQueue: () => Promise<void>;
  cleanupQueue: () => void;
  reconnect: () => void;
  onCallReady: (callback: () => void) => void;
}

const QUEUE_ID_KEY = 'queueId';
const POLLING_INTERVAL = 10000; // 10 seconds fallback polling

export function useQueue(): UseQueueReturn {
  const { user, getCredentials } = useAuth();
  const [isInQueue, setIsInQueue] = useState(false);
  const [queueId, setQueueId] = useState<string | null>(null);
  const [queueJoinTime, setQueueJoinTime] = useState<number | null>(null);
  const [queuePosition, setQueuePosition] = useState(0);
  const [queueLength, setQueueLength] = useState(0);
  const [estimatedWaitTime, setEstimatedWaitTime] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [callReadyCallback, setCallReadyCallback] = useState<(() => void) | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    switch (message.type) {
      case 'POSITION_UPDATE':
        if (message.position !== undefined) {
          setQueuePosition(message.position);
        }
        if (message.queueLength !== undefined) {
          setQueueLength(message.queueLength);
        }
        if (message.estimatedWaitTime !== undefined) {
          setEstimatedWaitTime(message.estimatedWaitTime);
        }
        break;

      case 'CALL_READY':
        // Don't clean up queue state yet - we need queueId and queueJoinTime for metrics
        websocketService.disconnect();
        setIsInQueue(false); // Hide queue UI
        localStorage.removeItem(QUEUE_ID_KEY);
        
        // Small delay to ensure state updates propagate
        setTimeout(async () => {
          if (callReadyCallback) {
            callReadyCallback();
          }
          // Clean up backend queue entry after call starts
          if (queueId) {
            try {
              const credentials = await getCredentials();
              await queueService.leaveQueue(queueId, credentials);
            } catch (err) {
              console.error('Failed to remove queue entry:', err);
            }
          }
        }, 100);
        break;

      case 'QUEUE_REMOVED':
        cleanupQueue();
        break;

      case 'ERROR':
        console.error('Queue error:', message.message);
        break;
    }
    debug.log('Received WebSocket message:', message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callReadyCallback]);

  // Restore queue state on mount
  useEffect(() => {
    const savedQueueId = localStorage.getItem(QUEUE_ID_KEY);
    if (savedQueueId && !isInQueue) {
      restoreQueueState(savedQueueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Set up WebSocket message handler
  useEffect(() => {
    websocketService.onMessage(handleWebSocketMessage);
    
    websocketService.onClose(() => {
      setIsConnected(false);
      // Start polling fallback if WebSocket disconnects
      startPolling();
    });

    return () => {
      websocketService.onMessage(() => {});
      websocketService.onClose(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleWebSocketMessage]);

  // Update WebSocket connection status
  useEffect(() => {
    const checkConnection = setInterval(() => {
      setIsConnected(websocketService.isConnected());
    }, 1000);

    return () => clearInterval(checkConnection);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (isInQueue) {
        websocketService.disconnect();
      }
    };
  }, [isInQueue, pollingInterval]);

  const restoreQueueState = async (savedQueueId: string) => {
    try {
      const credentials = await getCredentials();
      const status = await queueService.getQueueStatus(savedQueueId, credentials);
      
      if (status && status.status === 'QUEUED') {
        setQueueId(savedQueueId);
        setIsInQueue(true);
        setQueuePosition(status.position);
        setQueueLength(status.queueLength);
        setEstimatedWaitTime(status.estimatedWaitTime);

        // Reconnect WebSocket
        const config = await import('@/services/configService').then(m => m.configService.getConfig());
        if (config?.WS_API_ENDPOINT) {
          try {
            await websocketService.connect(config.WS_API_ENDPOINT, savedQueueId);
            setIsConnected(true);
          } catch (error) {
            debug.warn('WebSocket reconnection failed, using polling:', error);
            startPolling();
          }
        }
      } else {
        // Queue entry expired or completed
        localStorage.removeItem(QUEUE_ID_KEY);
      }
    } catch (error) {
      console.error('Failed to restore queue state:', error);
      localStorage.removeItem(QUEUE_ID_KEY);
    }
  };

  const startPolling = () => {
    if (pollingInterval || !queueId) return;

    const interval = setInterval(async () => {
      if (!queueId) {
        clearInterval(interval);
        return;
      }

      try {
        const credentials = await getCredentials();
        const status = await queueService.getQueueStatus(queueId, credentials);
        if (status) {
          setQueuePosition(status.position);
          setQueueLength(status.queueLength);
          setEstimatedWaitTime(status.estimatedWaitTime);
        } else {
          // Queue entry not found, clean up
          cleanupQueue();
          clearInterval(interval);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, POLLING_INTERVAL);

    setPollingInterval(interval);
  };

  const joinQueue = async (featureName?: string): Promise<void> => {
    // Generate anonymous user ID per session
    const userId = `anon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Get credentials for IAM signing
      const credentials = await getCredentials();
      
      const entry = await queueService.joinQueue(userId, credentials, featureName);
      
      setQueueId(entry.queueId);
      setQueueJoinTime(Date.now()); // Track when user joined queue
      setIsInQueue(true);
      setQueuePosition(entry.position);
      setQueueLength(entry.queueLength);
      setEstimatedWaitTime(entry.estimatedWaitTime);
      setIsConnected(websocketService.isConnected());

      // Save to localStorage for page refresh
      localStorage.setItem(QUEUE_ID_KEY, entry.queueId);

      // Start polling fallback if WebSocket not connected
      if (!websocketService.isConnected()) {
        startPolling();
      }
    } catch (error) {
      console.error('Failed to join queue:', error);
      throw error;
    }
  };

  const leaveQueue = async (): Promise<void> => {
    if (!queueId) return;

    try {
      const credentials = await getCredentials();
      await queueService.leaveQueue(queueId, credentials);
      cleanupQueue();
    } catch (error) {
      console.error('Failed to leave queue API call:', error);
      // Clean up local state even if API call fails - don't throw
      cleanupQueue();
    }
  };

  const cleanupQueue = () => {
    debug.log('Cleaning up queue state');
    websocketService.disconnect();
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setIsInQueue(false);
    setQueueId(null);
    setQueueJoinTime(null); // Clear join time
    setQueuePosition(0);
    setQueueLength(0);
    setEstimatedWaitTime(0);
    setIsConnected(false);
    localStorage.removeItem(QUEUE_ID_KEY);
    debug.log('Queue state cleaned up, isInQueue set to false');
  };

  const reconnect = () => {
    if (queueId) {
      const config = configService.getConfig();
      if (config?.WS_API_ENDPOINT) {
        websocketService.connect(config.WS_API_ENDPOINT, queueId)
          .then(() => setIsConnected(true))
          .catch((error) => {
            console.error('Manual reconnection failed:', error);
            startPolling();
          });
      }
    }
  };

  const onCallReady = (callback: () => void) => {
    setCallReadyCallback(() => callback);
  };

  return {
    isInQueue,
    queuePosition,
    queueLength,
    estimatedWaitTime,
    queueId,
    queueJoinTime,
    isConnected,
    joinQueue,
    leaveQueue,
    cleanupQueue,
    reconnect,
    onCallReady,
  };
}
